import type {
  CountryListParams,
  ICountry,
  IProvince,
  ProvinceListParams,
} from '@/shared/api/types/country-types'
import { useQuery } from '@tanstack/react-query'
import { getAllProvinces, getCountries, getProvinces } from '@/shared/api/services/country-service'

export const COUNTRY_QUERY_KEYS = {
  all: ['countries'] as const,
  list: (params: CountryListParams) => [...COUNTRY_QUERY_KEYS.all, 'list', params] as const,
  provinces: (params: ProvinceListParams) =>
    [...COUNTRY_QUERY_KEYS.all, 'provinces', params] as const,
  allProvinces: (countryIds: string[]) =>
    [...COUNTRY_QUERY_KEYS.all, 'all-provinces', countryIds.join(',')] as const,
}

export function useCountriesQuery(params: CountryListParams) {
  return useQuery({
    queryKey: COUNTRY_QUERY_KEYS.list(params),
    queryFn: async () => getCountries(params),
  })
}

export function useProvincesQuery(params: ProvinceListParams) {
  return useQuery({
    queryKey: COUNTRY_QUERY_KEYS.provinces(params),
    queryFn: async () => getProvinces(params),
  })
}

export function useAllProvincesQuery(countries: ICountry[]) {
  const safeCountries = Array.isArray(countries) ? countries : []
  const countryIds = safeCountries.map((item) => item.id)
  return useQuery<IProvince[]>({
    queryKey: COUNTRY_QUERY_KEYS.allProvinces(countryIds),
    enabled: countryIds.length > 0,
    staleTime: 1000 * 60 * 60 * 2,
    gcTime: 1000 * 60 * 60 * 4,
    queryFn: async () => {
      const response = await getAllProvinces(countryIds)
      return response.data
    },
  })
}
